import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Navbar from "../components/common/Navbar";
import SettingsSidebar from "../components/groupSettings/SettingsSidebar";
import GeneralSettingsTab from "../components/groupSettings/GeneralSettingsTab";
import ParticipantsTab from "../components/groupSettings/ParticipantsTab";
import CoverSettingsTab from "../components/groupSettings/CoverSettingsTab";
import WatermarkSettingsTab from "../components/groupSettings/WatermarkSettingsTab";
import useParticipants from "../hooks/groupSettings/useParticipants";
import useCoverEditor from "../hooks/groupSettings/useCoverEditor";
import useWatermarkEditor from "../hooks/groupSettings/useWatermarkEditor";
import useGroupSettings from "../hooks/groupSettings/useGroupSettings";
import "../styles/pages/GroupSettingsPage.css";

const GroupSettingsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("general"); // "general", "participants", "cover", "watermark"

  const {
    group,
    groupName,
    setGroupName,
    groupMedia,
    isLoading,
    isSaving,
    initialCoverState,
    initialWatermarkState,
    handleSave: handleGroupSave,
    handleSaveWatermark: handleGroupSaveWatermark,
    refreshGroup,
    handleDeleteGroup,
  } = useGroupSettings({ id });

  const {
    selectedFile,
    previewUrl,
    scale,
    setScale,
    posX,
    setPosX,
    posY,
    setPosY,
    isDragging,
    setIsDragging,
    handleFileChange,
    handleDrop,
  } = useCoverEditor({ initialCoverState });

  // 💧 Watermark States
  const {
    wmEnabled,
    setWmEnabled,
    wmFile,
    wmPreviewUrl,
    wmPosition,
    setWmPosition,
    wmPosX,
    setWmPosX,
    wmPosY,
    setWmPosY,
    wmScale,
    setWmScale,
    wmOpacity,
    setWmOpacity,
    selectedSamplePhotoIndex,
    setSelectedSamplePhotoIndex,
    isWmDragging,
    setIsWmDragging,
    wmCanvasRef,
    handleWmFileChange,
    handleWmDrop,
    handleWmCanvasMouseDown,
    handleWmCanvasMouseMove,
    handleWmCanvasMouseUp,
    applyPresetPosition,
  } = useWatermarkEditor({ initialWatermarkState });



  const handleCopyCode = () => {
    if (group?.code) {
      navigator.clipboard.writeText(group.code);
      toast.success("📋 Group Access Code copied to clipboard!");
    }
  };

  const handleSave = () => {
    handleGroupSave({
      groupName,
      selectedFile,
      scale,
      posX,
      posY,
    });
  };

  const handleSaveWatermark = () => {
    handleGroupSaveWatermark({
      wmEnabled,
      wmFile,
      wmPosition,
      wmPosX,
      wmPosY,
      wmScale,
      wmOpacity,
    });
  };

  const {
    participants,
    handleTogglePermission,
    handleRemoveUserFromGroup,
  } = useParticipants({
    group,
    groupId: id,
    onSuccess: refreshGroup,
  });

  if (isLoading) {
    return (
      <div className="group-settings-container">
        <Navbar />
        <div style={{ textAlign: "center", padding: "80px 20px" }}>
          <div className="spinner" style={{ margin: "0 auto 16px" }}></div>
          <h3>Loading Group Settings...</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="group-settings-container">
      <Navbar />

      {/* Top Header Navigation */}
      <div className="settings-header-nav">
        <button
          className="settings-back-btn"
          onClick={() => navigate(`/group/${id}`)}
        >
          ← Back to Album
        </button>
      </div>

      <div className="settings-main-wrapper">
        <div className="settings-page-title-row">
          <h1 className="settings-page-title">⚙️ Group Settings</h1>
          <p className="settings-page-subtitle">
            Manage settings, participants access, and cover image for {group?.name}
          </p>
        </div>

        {/* Sidebar + Content Layout */}
        <div className="settings-sidebar-layout">
          <SettingsSidebar
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            participantsCount={participants.length}
          />

          <div className="settings-content-panel">
            {activeTab === "general" && (
              <GeneralSettingsTab
                group={group}
                groupName={groupName}
                setGroupName={setGroupName}
                handleCopyCode={handleCopyCode}
                handleSave={handleSave}
                isSaving={isSaving}
                handleDeleteGroup={handleDeleteGroup}
              />
            )}

            {activeTab === "participants" && (
              <ParticipantsTab
                participants={participants}
                handleTogglePermission={handleTogglePermission}
                handleRemoveUserFromGroup={handleRemoveUserFromGroup}
              />
            )}

            {activeTab === "cover" && (
              <CoverSettingsTab
                isDragging={isDragging}
                setIsDragging={setIsDragging}
                handleDrop={handleDrop}
                handleFileChange={handleFileChange}
                previewUrl={previewUrl}
                posX={posX}
                setPosX={setPosX}
                posY={posY}
                setPosY={setPosY}
                scale={scale}
                setScale={setScale}
                handleSave={handleSave}
                isSaving={isSaving}
                groupName={groupName || group?.name}
              />
            )}

            {activeTab === "watermark" && (
              <WatermarkSettingsTab
                wmEnabled={wmEnabled}
                setWmEnabled={setWmEnabled}
                isWmDragging={isWmDragging}
                setIsWmDragging={setIsWmDragging}
                handleWmDrop={handleWmDrop}
                handleWmFileChange={handleWmFileChange}
                wmPosition={wmPosition}
                applyPresetPosition={applyPresetPosition}
                groupMedia={groupMedia}
                selectedSamplePhotoIndex={selectedSamplePhotoIndex}
                setSelectedSamplePhotoIndex={setSelectedSamplePhotoIndex}
                wmCanvasRef={wmCanvasRef}
                handleWmCanvasMouseDown={handleWmCanvasMouseDown}
                handleWmCanvasMouseMove={handleWmCanvasMouseMove}
                handleWmCanvasMouseUp={handleWmCanvasMouseUp}
                wmPreviewUrl={wmPreviewUrl}
                wmScale={wmScale}
                setWmScale={setWmScale}
                wmOpacity={wmOpacity}
                setWmOpacity={setWmOpacity}
                wmPosX={wmPosX}
                setWmPosX={setWmPosX}
                wmPosY={wmPosY}
                setWmPosY={setWmPosY}
                setWmPosition={setWmPosition}
                handleSaveWatermark={handleSaveWatermark}
                isSaving={isSaving}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupSettingsPage;
